const MAX_PANEL_CHANNELS = 8;
const MAX_EXTRA_CHANNELS = MAX_PANEL_CHANNELS - 1;
const PANEL_COLOR_MODES = new Set(['auto', 'channel', 'lap']);

function channelName(key) {
  if (typeof key !== 'string') return '';
  const marker = key.indexOf('::');
  return marker === -1 ? key : key.slice(marker + 2);
}

export function normalizeChannelPanelConfig(baseName, config) {
  const seen = new Set();
  const additionalChannelKeys = [];
  for (const key of Array.isArray(config?.additionalChannelKeys) ? config.additionalChannelKeys : []) {
    if (typeof key !== 'string' || !key || channelName(key) === baseName || seen.has(key)) continue;
    seen.add(key);
    additionalChannelKeys.push(key);
    if (additionalChannelKeys.length === MAX_EXTRA_CHANNELS) break;
  }
  return {
    additionalChannelKeys,
    split: config?.split === true,
    sharedScale: config?.sharedScale === true,
    colorMode: PANEL_COLOR_MODES.has(config?.colorMode) ? config.colorMode : 'auto',
  };
}

export function normalizeChannelPanelConfigs(configs) {
  if (!configs || typeof configs !== 'object' || Array.isArray(configs)) return {};
  return Object.fromEntries(
    Object.entries(configs)
      .filter(([baseName]) => typeof baseName === 'string' && baseName)
      .map(([baseName, config]) => [baseName, normalizeChannelPanelConfig(baseName, config)])
  );
}

export function resolvePanelExtraChannels(baseName, storedKeys, resolveChannel) {
  if (typeof resolveChannel !== 'function') return [];
  return (Array.isArray(storedKeys) ? storedKeys : [])
    .map(resolveChannel)
    .filter(channel => channel && channel.name !== baseName)
    .slice(0, MAX_EXTRA_CHANNELS);
}

export function partitionPanelChannelOptions(availableChannels, selectedChannels, filter = '') {
  const needle = String(filter).trim().toLowerCase();
  const matching = (Array.isArray(availableChannels) ? availableChannels : []).filter(channel => (
    !needle || channel.name.toLowerCase().includes(needle)
  ));
  const matchingByKey = new Map(matching.map(channel => [channel.key, channel]));
  const selected = (Array.isArray(selectedChannels) ? selectedChannels : [])
    .map(channel => matchingByKey.get(channel.key))
    .filter(Boolean);
  const selectedKeys = new Set(selected.map(channel => channel.key));
  return {
    selected,
    available: matching.filter(channel => !selectedKeys.has(channel.key)),
  };
}

export function effectivePanelColorMode(colorMode, signalInputs) {
  if (colorMode === 'channel' || colorMode === 'lap') return colorMode;
  const hasMultipleTraces = (Array.isArray(signalInputs) ? signalInputs : []).some(signal => {
    const primaryCount = Array.isArray(signal.selectedLaps) ? signal.selectedLaps.length : 0;
    const comparisonCount = (Array.isArray(signal.compareDrivers) ? signal.compareDrivers : [])
      .reduce((total, driver) => total + (
        Array.isArray(driver.selectedLaps) ? driver.selectedLaps.length : 0
      ), 0);
    return primaryCount + comparisonCount > 1;
  });
  return hasMultipleTraces ? 'lap' : 'channel';
}

export function togglePanelExtraChannel(configs, baseName, channelKey) {
  const current = normalizeChannelPanelConfig(baseName, configs?.[baseName]);
  const exists = current.additionalChannelKeys.includes(channelKey);
  const additionalChannelKeys = exists
    ? current.additionalChannelKeys.filter(key => key !== channelKey)
    : current.additionalChannelKeys.length < MAX_EXTRA_CHANNELS
      ? [...current.additionalChannelKeys, channelKey]
      : current.additionalChannelKeys;
  return {
    ...normalizeChannelPanelConfigs(configs),
    // Axis compatibility depends on the exact channel composition. Require an
    // explicit opt-in again whenever that composition changes.
    [baseName]: { ...current, additionalChannelKeys, sharedScale: false },
  };
}

export function setPanelSplit(configs, baseName, split) {
  const current = normalizeChannelPanelConfig(baseName, configs?.[baseName]);
  return {
    ...normalizeChannelPanelConfigs(configs),
    [baseName]: { ...current, split: !!split },
  };
}

export function setPanelSharedScale(configs, baseName, sharedScale) {
  const current = normalizeChannelPanelConfig(baseName, configs?.[baseName]);
  return {
    ...normalizeChannelPanelConfigs(configs),
    [baseName]: { ...current, sharedScale: !!sharedScale },
  };
}

export function setPanelColorMode(configs, baseName, colorMode) {
  const current = normalizeChannelPanelConfig(baseName, configs?.[baseName]);
  return {
    ...normalizeChannelPanelConfigs(configs),
    [baseName]: {
      ...current,
      colorMode: PANEL_COLOR_MODES.has(colorMode) ? colorMode : 'auto',
    },
  };
}

export function removePanelConfig(configs, baseName) {
  const next = { ...normalizeChannelPanelConfigs(configs) };
  delete next[baseName];
  return next;
}

export { MAX_EXTRA_CHANNELS, MAX_PANEL_CHANNELS };
